import {useEffect, useState} from 'react'
import {useParams} from 'react-router'
import axios from 'axios'
import {
  useWt,
  useWuShowToast,
  WuButton,
  WuIcon,
  WuLoader,
  WuTextarea,
  WuTooltip,
} from '@npm-questionpro/wick-ui-lib'
import {
  IDialogDetails,
  IRestrictedUser,
  IStoredDialogUserAvatar,
} from '../../../../types'
import {dialogApi} from '../../../../api/dialogApi'
import {DIALOG} from '../../../../Translation/English/dialog'
import {GLOBAL} from '../../../../Translation/English/global'
import {ResponsesList} from '../../../Dialog/components/ResponsesList/ResponsesList'
import styles from './UserResponsePage.module.scss'

interface IUserResponsePageProps {
  dialogDetails: IDialogDetails | undefined
  onInvalidAvatar?: () => void
}

export const UserResponsePage = ({
  dialogDetails,
  onInvalidAvatar,
}: IUserResponsePageProps): React.ReactElement => {
  const addDialogResponseMutation = dialogApi.useAddDialogResponse()
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const {dialogId} = useParams<{dialogId: string}>()

  const [response, setResponse] = useState<string>('')

  const [sessionUserId, setSessionUserId] = useState<string | null>(
    (): string => {
      const sessionIds = JSON.parse(
        localStorage.getItem(`dialogUserIds`) || '{}',
      )
      const orgId = dialogDetails?.orgId ?? ''
      if (!sessionIds[orgId]) {
        sessionIds[orgId] = crypto.randomUUID()
        localStorage.setItem(`dialogUserIds`, JSON.stringify(sessionIds))
      }
      return sessionIds[orgId]
    },
  )

  const isUserRestricted = (): boolean => {
    const data = localStorage.getItem(`dialogUserAvatars`)
    const parsedData = data && JSON.parse(data)
    if (
      !dialogDetails ||
      !Array.isArray(parsedData) ||
      !Array.isArray(dialogDetails.restrictedUsers)
    )
      return false
    const currentUser = parsedData.find(
      (item: IStoredDialogUserAvatar) => item.orgId === dialogDetails.orgId,
    )
    if (!currentUser) return false
    return dialogDetails.restrictedUsers.some(
      (user: IRestrictedUser) => user.username === currentUser.username,
    )
  }

  useEffect(() => {
    if (!sessionUserId) {
      const sessionIds = JSON.parse(
        localStorage.getItem(`dialogUserIds`) || '{}',
      )
      const orgId = dialogDetails?.orgId ?? ''
      if (!sessionIds[orgId]) {
        sessionIds[orgId] = crypto.randomUUID()
        localStorage.setItem(`dialogUserIds`, JSON.stringify(sessionIds))
      }
      setSessionUserId(sessionIds[orgId])
    }
  }, [sessionUserId, dialogDetails?.orgId])

  const RESTRICTED_MESSAGE =
    'You have been restricted to further participate in this conversation.'

  const showRestrictedToast = (): void => {
    showToast({message: RESTRICTED_MESSAGE, variant: 'error'})
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (isUserRestricted()) {
      showRestrictedToast()
      return
    }
    setResponse(e.target.value)
  }

  const addUserResponse = async (): Promise<void> => {
    const data = localStorage.getItem(`dialogUserAvatars`)
    const parsedData = data && JSON.parse(data)
    if (!Array.isArray(parsedData)) return
    const currentUser =
      parsedData.find(
        (item: IStoredDialogUserAvatar) => item.orgId === dialogDetails?.orgId,
      ) ?? null
    if (!currentUser) return

    try {
      await addDialogResponseMutation.mutateAsync({
        text: response,
        dialogId,
        dialogUserAvtarId: currentUser.dialogUserAvatarId,
        dialogAvatarImageId: currentUser.dialogAvatarImageId,
        sessionId: sessionUserId,
        isModerator: false,
      })
      setResponse('')
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        onInvalidAvatar?.()
      } else {
        showToast({
          message: wt(DIALOG.somethingWentWrongTryAgain),
          variant: 'error',
        })
      }
    }
  }

  return (
    <div className={styles.userResponse}>
      <div className={styles.userResponseQuestion}>
        {dialogDetails?.questionText}
      </div>

      <div className={styles.scrollRegion}>
        {dialogDetails?.responses && dialogDetails.responses.length > 0 ? (
          <>
            <div className={styles.userResponseListHeader}>
              {wt(DIALOG.comments)}
              <span className={styles.userResponseCount}>
                {dialogDetails.responses.length}
              </span>
            </div>
            <ResponsesList
              responses={dialogDetails.responses}
              dialogDetails={dialogDetails}
              isUserModerator={false}
            />
          </>
        ) : (
          <div className={styles.userResponseEmpty}>
            <WuIcon icon="wm-forum" className={styles.userResponseEmptyIcon} />
            {wt(DIALOG.noComments)}
          </div>
        )}
      </div>

      <div className={styles.composer}>
        {addDialogResponseMutation.isPending && (
          <div className={styles.loadingOverlay}>
            <WuLoader size="sm" />
          </div>
        )}
        <WuTextarea
          variant="flat"
          placeholder={wt(DIALOG.addComment)}
          value={response}
          onChange={handleChange}
          onClick={() => {
            if (isUserRestricted()) {
              showRestrictedToast()
            }
          }}
          disabled={addDialogResponseMutation.isPending}
          className={styles.composerInput}
          data-testid="user-response-input"
        />
        <div className={styles.composerToolbar}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={() => setResponse('')}
            disabled={
              response.length < 1 || addDialogResponseMutation.isPending
            }
          >
            {wt(GLOBAL.cancel)}
          </button>
          <WuTooltip Content={wt(DIALOG.submit)}>
            <WuButton
              iconOnly
              Icon={<WuIcon icon="wm-send" />}
              aria-label={wt(DIALOG.submit)}
              onClick={() => void addUserResponse()}
              disabled={
                response.length < 1 ||
                isUserRestricted() ||
                addDialogResponseMutation.isPending
              }
              data-testid="user-response-btn"
            />
          </WuTooltip>
        </div>
      </div>
    </div>
  )
}
